// react
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// material ui
import { Box, Button, Grid, Skeleton, Stack, useTheme } from '@mui/material';
import {
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';

// api
import {
  useUnitsDeleteTagAsyncByidtagMutation,
  useUnitsGetCollectionAsyncByqueryOptionsQuery,
  useUnitsPostTagAsyncByidnewTagMutation,
} from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import ApiResponseAlert from '@/components/ui/alert/ApiResponseAlert';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import AggregationSelector from '@/features/dashboards/components/AggregationSelector';
import DashboardDateRangeSelector from '@/features/dashboards/components/DashboardDateRangeSelector';
import DashboardToolbar from '@/features/dashboards/components/DashboardToolbar';

// components
import useSearchParams from '@/hooks/useStableSearchParams';
import { showSnackbar } from '@/redux/snackbarSlice';
import { formatNumber } from '@/utils/formatters';

// relative
import DashboardCard from '../../components/DashboardCard';
import DashboardDatagrid from '../../components/DashboardDatagrid';
import Kpi from '../../components/Kpi';
import CompressedAirConsumptionChart from './components/CompressedAirConsumptionChart';
import SeriesChart from './components/SeriesChart';
import UnitPairDialog from './components/UnitPairDialog';
import useCompressedAirData from './hooks/useCompressedAirData';
import {
  calculateEfficiency,
  calculateMatchedTotals,
  calculateSpecificEnergy,
  calculateTotalAirVolume,
  calculateTotalEnergy,
  filterUnitsByDataType,
  findUnitPairs,
  FLOW_DATATYPE,
  getPairedUnitsOnly,
  PAIR_TAG_PREFIX,
  POWER_DATATYPE,
  prepareConsumptionChartData,
  preparePatternChartData,
} from './utils';

const CompressedAirBasic = () => {
  const { t } = useTranslation('dashboards-compressed-air-basic');
  const { accountId } = useParams({ strict: false });
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const dispatch = useDispatch();

  const startTimestamp = searchParams.get('from');
  const endTimestamp = searchParams.get('to');
  const agg = searchParams.get('agg') || 'day';
  const selectedUnitIdsParam = searchParams.get('units');

  const [dialogOpen, setDialogOpen] = useState(false);
  const isInitialized = useRef(false);

  // Get units first
  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${accountId})` },
    {
      skip: !accountId,
    },
  );

  // Calculate unit pairs from tags
  const unitPairs = useMemo(() => {
    if (!units) return [];
    return findUnitPairs(units);
  }, [units]);

  // Get only paired units for display
  const pairedUnits = useMemo(() => {
    if (!units) return [];
    return getPairedUnitsOnly(units, unitPairs);
  }, [units, unitPairs]);
  // Parse selected unit IDs from URL, default to all paired units
  const selectedUnitIds = useMemo(() => {
    if (selectedUnitIdsParam === 'none') return [];
    if (selectedUnitIdsParam) {
      return selectedUnitIdsParam.split(',').map((id) => Number(id));
    }
    // Default: all paired units
    return pairedUnits.map((unit) => unit.UnitId);
  }, [selectedUnitIdsParam, pairedUnits]);

  // Fetch compressed air data using custom hook
  // Fetches data for ALL paired units (for datagrid)
  // Aggregates only SELECTED units (for charts/KPIs)
  const {
    data: BasicEnergyData,
    isFetching,
    isLoading,
    isError,
    error,
  } = useCompressedAirData({
    pairedUnits,
    selectedUnitIds,
    startTimestamp,
    endTimestamp,
    aggregation: agg,
  });

  const [postUnitTag] = useUnitsPostTagAsyncByidnewTagMutation();
  const [deleteUnitTag] = useUnitsDeleteTagAsyncByidtagMutation();

  // Filter units by data type for dialog
  const flowUnits = useMemo(() => {
    if (!units) return [];
    return filterUnitsByDataType(units, FLOW_DATATYPE);
  }, [units]);

  const powerUnits = useMemo(() => {
    if (!units) return [];
    return filterUnitsByDataType(units, POWER_DATATYPE);
  }, [units]);

  // Calculate KPIs
  const totalVolume = useMemo(() => {
    if (!BasicEnergyData?.MeterSummaries) return 0;
    return calculateTotalAirVolume(
      BasicEnergyData.MeterSummaries,
      selectedUnitIds.length > 0 ? selectedUnitIds : null,
    );
  }, [BasicEnergyData, selectedUnitIds]);

  const totalEnergy = useMemo(() => {
    if (!BasicEnergyData?.MeterSummaries) return 0;
    return calculateTotalEnergy(
      BasicEnergyData.MeterSummaries,
      selectedUnitIds.length > 0 ? selectedUnitIds : null,
    );
  }, [BasicEnergyData, selectedUnitIds]);

  const matchedTotals = useMemo(() => {
    if (!BasicEnergyData?.HourData) {
      return { totalVolume: 0, totalEnergy: 0 };
    }

    return calculateMatchedTotals(BasicEnergyData.HourData);
  }, [BasicEnergyData]);

  const efficiency = useMemo(() => {
    return calculateEfficiency(
      matchedTotals.totalVolume,
      matchedTotals.totalEnergy,
    );
  }, [matchedTotals]);

  const specificEnergy = useMemo(() => {
    return calculateSpecificEnergy(
      matchedTotals.totalEnergy,
      matchedTotals.totalVolume,
    );
  }, [matchedTotals]);

  // Prepare chart data (uses aggregated data from selected units only)
  const consumptionChartData = useMemo(() => {
    if (!BasicEnergyData) {
      return { columnSeries: [], lineSeries: [], visibleData: [] };
    }
    return prepareConsumptionChartData(
      BasicEnergyData,
      unitPairs,
      { start: startTimestamp, end: endTimestamp },
      agg,
      theme.palette.compressedAir.dark,
    );
  }, [BasicEnergyData, unitPairs, startTimestamp, endTimestamp, agg, theme]);

  const patternChartData = useMemo(() => {
    if (!BasicEnergyData) return [];
    return preparePatternChartData(BasicEnergyData, unitPairs, agg);
  }, [BasicEnergyData, unitPairs, agg]);

  // Table columns
  const tableColumns = [
    {
      field: 'group_key',
      headerName: t('table.columns.group_key'),
      valueFormatter: (value) => value.split(':')[1] || value,
      flex: 0.3,
    },
    {
      field: 'name',
      headerName: t('table.columns.name'),
      flex: 0.5,
      renderCell: (params) => {
        const hasTags = params.row.tags && params.row.tags.length > 0;
        return (
          <Stack spacing={0.5} sx={{ py: hasTags ? 1 : 0.5 }}>
            <Box>{params.value}</Box>
            {hasTags && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {params.row.tags.map((tag, index) => (
                  <Box
                    key={index}
                    sx={{
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tag.Text}
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'volume',
      headerName: t('table.columns.volume'),
      type: 'number',
      flex: 0.4,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: 'energy',
      headerName: t('table.columns.energy'),
      type: 'number',
      flex: 0.4,
      valueFormatter: (value) => formatNumber(value),
    },
  ];

  const tableData = useMemo(() => {
    const rows = BasicEnergyData?.MeterSummaries?.filter((ms) =>
      pairedUnits.some((u) => u.UnitId === ms.UnitId),
    ).map((ms) => {
      const unit = units?.find((u) => u.UnitId === ms.UnitId);
      return {
        id: ms.UnitId,
        group_key:
          unit?.Tags?.find((tag) => tag.Text.startsWith(PAIR_TAG_PREFIX))
            ?.Text || 'Ungrouped',
        tags: unit?.Tags || [],
        name: ms.Name,
        volume: ms.TotalVolume || 0,
        energy: ms.TotalEnergy || 0,
      };
    });
    return rows || [];
  }, [BasicEnergyData, units, pairedUnits]);
  const apiRef = useGridApiRef();

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: { model: ['group_key'] },
      aggregation: { model: { volume: 'sum', energy: 'sum' } },
    },
  });

  // Mark as initialized once we have table data
  useEffect(() => {
    if (tableData.length > 0 && !isInitialized.current) {
      isInitialized.current = true;
    }
  }, [tableData]);

  // Handle unit selection via row selection
  const handleRowSelectionChange = (model) => {
    // Don't respond to DataGrid callbacks until data is loaded
    if (!isInitialized.current) {
      return;
    }

    // Normalise to selected leaf IDs regardless of model type.
    // - include model: model.ids = selected rows (may include group row IDs)
    // - exclude model: model.ids = excluded rows; everything else is selected
    const pairedUnitIdSet = new Set(pairedUnits.map((u) => u.UnitId));
    let selectedLeafIds;
    if (model.type === 'exclude') {
      selectedLeafIds = pairedUnits
        .map((u) => u.UnitId)
        .filter((id) => !model.ids.has(id));
    } else {
      selectedLeafIds = Array.from(model.ids).filter((id) =>
        pairedUnitIdSet.has(id),
      );
    }

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (selectedLeafIds.length === pairedUnits.length) {
        newParams.delete('units');
      } else if (selectedLeafIds.length === 0) {
        newParams.set('units', 'none');
      } else {
        newParams.set('units', selectedLeafIds.join(','));
      }
      return newParams;
    });
  };

  // Handle unit pair creation
  const handleCreatePair = async ({ flowUnitId, powerUnitId, pairTag }) => {
    const tagText = `${PAIR_TAG_PREFIX}${pairTag}`;

    // Add tag to both units
    const result1 = await postUnitTag({
      id: flowUnitId,
      createTagDto: { Text: tagText },
    });
    if ('error' in result1) throw result1.error;
    const result2 = await postUnitTag({
      id: powerUnitId,
      createTagDto: { Text: tagText },
    });
    if ('error' in result2) throw result2.error;

    dispatch(
      showSnackbar({
        message: t('messages.pairCreated'),
        severity: 'success',
      }),
    );
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  // Handle unit pair deletion
  const handleDeletePair = async (pairTag, flowUnit, powerUnit) => {
    const tagText = `${PAIR_TAG_PREFIX}${pairTag}`;

    // Remove tag from both units
    if (flowUnit) {
      const result1 = await deleteUnitTag({
        id: flowUnit.UnitId,
        tag: tagText,
      });
      if ('error' in result1) throw result1.error;
    }
    if (powerUnit) {
      const result2 = await deleteUnitTag({
        id: powerUnit.UnitId,
        tag: tagText,
      });
      if ('error' in result2) throw result2.error;
    }

    dispatch(
      showSnackbar({
        message: t('messages.pairDeleted'),
        severity: 'success',
      }),
    );
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  if (isError) {
    return (
      <ApiResponseAlert
        severity="error"
        title={t('messages.loadError')}
        object={error}
        goBack
      />
    );
  }

  if (isLoading || !BasicEnergyData) {
    return (
      <Box>
        <DashboardToolbar
          title={t('title')}
          rightSlot={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DashboardDateRangeSelector />
            </Box>
          }
        />
        <Grid container spacing={2} alignItems="stretch">
          <Grid
            size={{
              xs: 12,
              md: 5
            }}>
            <Stack gap={2} height="100%">
              <Skeleton variant="rectangular" animation="wave" height={240} />
              <Skeleton variant="rectangular" animation="wave" height={360} />
            </Stack>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 7
            }}>
            <Skeleton variant="rectangular" animation="wave" height={700} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <DashboardToolbar
        title={t('title')}
        rightSlot={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DashboardDateRangeSelector />
          </Box>
        }
      />
      <Grid container spacing={2} alignItems="stretch">
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Stack gap={2} height="100%">
            {/* KPI Card */}
            <DashboardCard
              title={t('cards.totalConsumption')}
              icon={<SpeedRoundedIcon />}
              iconColor={theme.palette.compressedAir.main}
              iconBgColor={theme.palette.compressedAir.light}
            >
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Kpi
                    value={formatNumber(totalVolume)}
                    unit={t('kpi.flowUnit')}
                  />
                  <Box
                    sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}
                  >
                    {t('kpi.totalVolume')}
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Kpi
                    value={formatNumber(totalEnergy)}
                    unit={t('kpi.energyUnit')}
                  />
                  <Box
                    sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}
                  >
                    {t('kpi.totalEnergy')}
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Kpi
                    value={formatNumber(efficiency)}
                    unit={t('kpi.efficiencyUnit')}
                  />
                  <Box
                    sx={{
                      textAlign: 'center',
                      mt: 1,
                      color: 'text.secondary',
                    }}
                    display={'flex'}
                    justifyContent={'center'}
                    flexDirection={'row'}
                  >
                    {t('kpi.efficiency')}
                    <InfoTooltipIcon title={t('kpi.efficiencyTooltip')} />
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Kpi
                    value={formatNumber(specificEnergy, 'da-DK', 2)}
                    unit={t('kpi.specificEnergyUnit')}
                  />
                  <Box
                    sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}
                  >
                    {t('kpi.specificEnergy')}
                  </Box>
                </Grid>
              </Grid>
            </DashboardCard>

            {/* Units Card */}
            <DashboardCard
              title={t('cards.units')}
              icon={<SensorsRoundedIcon />}
              iconColor={theme.palette.compressedAir.main}
              iconBgColor={theme.palette.compressedAir.light}
              actions={
                <Button
                  startIcon={<SettingsIcon />}
                  size="small"
                  onClick={() => setDialogOpen(true)}
                >
                  {t('actions.managePairs')}
                </Button>
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              {unitPairs.length === 0 ? (
                <Box
                  sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}
                >
                  {t('messages.noPairsFound')}
                </Box>
              ) : (
                <DashboardDatagrid
                  rows={tableData}
                  columns={tableColumns}
                  checkboxSelection
                  onSelectionChange={handleRowSelectionChange}
                  getRowHeight={() => 'auto'}
                  rowGroupingModel={['group_key']}
                  initialState={initialState}
                  apiRef={apiRef}
                />
              )}
            </DashboardCard>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <Stack gap={2}>
            {/* Consumption Chart */}
            <DashboardCard
              icon={<BarChartRoundedIcon />}
              iconColor={theme.palette.compressedAir.main}
              iconBgColor={theme.palette.compressedAir.light}
              title={t(`cards.consumptionAggregated`, {
                aggregate: t(`aggregate.${agg}`),
              })}
              actions={<AggregationSelector />}
            >
              <CompressedAirConsumptionChart
                columnSeries={consumptionChartData.columnSeries}
                lineSeries={consumptionChartData.lineSeries}
                visibleData={consumptionChartData.visibleData}
                height={400}
                isLoading={isFetching}
              />
            </DashboardCard>

            {/* Pattern Chart */}
            <DashboardCard
              icon={<BarChartRoundedIcon />}
              iconColor={theme.palette.compressedAir.main}
              iconBgColor={theme.palette.compressedAir.light}
              title={t('cards.consumptionPattern')}
            >
              <SeriesChart
                series={patternChartData}
                yAxes={[{ title: t('chart.volume'), opposite: false }]}
                height={300}
                isLoading={isFetching}
              />
            </DashboardCard>
          </Stack>
        </Grid>
      </Grid>

      {/* Unit Pair Dialog */}
      <UnitPairDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        existingPairs={unitPairs}
        availableFlowUnits={flowUnits}
        availablePowerUnits={powerUnits}
        onCreatePair={handleCreatePair}
        onDeletePair={handleDeletePair}
      />
    </Box>
  );
};

export default CompressedAirBasic;
